import axios from "axios";
import React, { useEffect } from "react";
import { Redirect, Route, RouteProps } from "react-router";

interface IAuthContext {
  authenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  getAuthToken: () => string | null;
}

interface LoginResponseDTO {
  accessToken: string;
}

const AuthContext = React.createContext<IAuthContext | null>(null);

export class AuthPersistenceService {
  static getAuthToken() {
    return localStorage.getItem("authToken");
  }

  static setAuthToken(token: string) {
    localStorage.setItem("authToken", token);
  }

  static removeAuthToken() {
    localStorage.removeItem("authToken");
  }
}

export const AuthProvider: React.FC = ({ children }) => {
  const [authToken, setAuthToken] = React.useState<string | null>(null);

  useEffect(() => {
    const token = AuthPersistenceService.getAuthToken();

    if (token) {
      setAuthToken(token);
    } else {
      setAuthToken(null);
    }
  }, []);

  const login = async (username: string, password: string) => {
    const result = await axios.post<LoginResponseDTO>("/login", { username, password });
    AuthPersistenceService.setAuthToken(result.data.accessToken);
    setAuthToken(result.data.accessToken);
  };

  const getAuthToken = () => authToken;

  const logout = () => {
    AuthPersistenceService.removeAuthToken();
    setAuthToken(null);
  };

  const authenticated = authToken != null;

  return <AuthContext.Provider value={{ authenticated, login, logout, getAuthToken }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function ProtectedRoute({ children, ...rest }: RouteProps) {
  const auth = useAuth();

  if (!auth.authenticated) {
    return <Redirect to="/login" />;
  }

  return <Route {...rest}>{children}</Route>;
}
